import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageBreadcrumb } from '../../components/library/page-breadcrumb/PageBreadcrumb';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';

import Toolbar, { Item } from 'devextreme-react/toolbar';
import { DataGridRef } from 'devextreme-react/data-grid';
import { SortableRef } from 'devextreme-react/sortable';
import { GanttRef } from 'devextreme-react/gantt';
import { exportGantt as exportGanttToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid } from 'devextreme/pdf_exporter';
import { exportDataGrid as exportDataGridXSLX } from 'devextreme/excel_exporter';
import LoadPanel from 'devextreme-react/load-panel';

import { TaskListGrid, TaskListKanban, TaskListGantt, FormPopup, TaskFormDetails } from '../../components';

import { newTask as newTaskDefaults } from '../../shared/constants';
import { useScreenSize } from '../../utils/media-query';

import { getTasks, getFilteredTasks } from '../../data/template-data';

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

import './planning-task-list.scss';
import Button from 'devextreme-react/button';
import TextBox, { TextBoxTypes } from 'devextreme-react/text-box';
import Tabs from 'devextreme-react/tabs';
import notify from 'devextreme/ui/notify';
import { Task } from '../../types/task';

const listsData = ['List', 'Kanban Board', 'Gantt'];

export const PlanningTaskList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef<DataGridRef>(null);
  const kanbanRef = useRef<SortableRef>(null);
  const ganttRef = useRef<GanttRef>(null);

  const [listView, kanbanView, ganttView] = listsData;

  const [view, setView] = useState(listView);
  const [index, setIndex] = useState(0);
  const [gridData, setGridData] = useState<Task[]>([]);
  const [filteredData, setFilteredData] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [formTaskInitData, setFormTaskInitData] = useState({ ...newTaskDefaults });
  const [popupVisible, setPopupVisible] = useState(false);

  const { isXSmall } = useScreenSize();

  const isDataGrid = view === listView;
  const isKanban = view === kanbanView;

  let newTaskData = { ...newTaskDefaults };

  const changePopupVisibility = useCallback((isVisible) => {
    setPopupVisible(isVisible);
  }, []);

  useEffect(() => {
    Promise.all([
      getTasks()
        .then((data) => setGridData(data)),
      getFilteredTasks()
        .then((data) => setFilteredData(data))
    ]).catch((error) => console.log(error));
  }, []);

  const onSaveClick = () => {
    notify({
      message: `New task "${newTaskData.text}" saved`,
      position: { at: 'bottom center', my: 'bottom center' }
    },
    'success'
    );

    setFormTaskInitData({ ...newTaskDefaults });
  };

  useEffect(() => {
    if (filteredData.length && gridData.length) {
      setLoading(false);
    }
  }, [filteredData, gridData]);

  const onDataChanged = useCallback((data) => {
    newTaskData = data;
  }, []);

  const viewToParam: Record<string, string> = {
    [listView]: 'list',
    [kanbanView]: 'kanban-board',
    [ganttView]: 'gantt'
  };

  const paramToView: Record<string, string> = Object.fromEntries(
    Object.entries(viewToParam).map(([k, v]) => [v, k])
  );

  useEffect(() => {
    const qp = searchParams.get('view');
    if (qp && paramToView[qp]) {
      const desiredView = paramToView[qp];
      if (desiredView !== view) {
        setView(desiredView);
        setIndex(listsData.findIndex((d) => d === desiredView));
      }
    } else if (!qp) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', viewToParam[view]);
        return next;
      });
    }
  }, [searchParams, view, listsData, setSearchParams]);

  const onTabClick = useCallback((e: { itemData?: string }) => {
    const newView = e.itemData || '';
    setView(newView);
    setIndex(listsData.findIndex((d) => d === newView));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const qpValue = viewToParam[newView] || viewToParam[listView];
      next.set('view', qpValue);
      return next;
    });
  }, [listsData, setSearchParams]);

  const onAddTaskClick = useCallback(() => {
    setFormTaskInitData({ ...newTaskDefaults });
    setPopupVisible(true);
  }, []);

  const refresh = useCallback(() => {
    if (isDataGrid) {
      gridRef.current?.instance().refresh();
    } else if (isKanban) {
      kanbanRef.current?.instance().update();
    } else {
      ganttRef.current?.instance().refresh();
    }
  }, [view]);

  const showColumnChooser = useCallback(() => {
    gridRef.current?.instance().showColumnChooser();
  }, []);

  const exportToPDF = useCallback(() => {
    if (isDataGrid) {
      const doc = new jsPDF();
      exportDataGrid({
        jsPDFDocument: doc,
        component: gridRef.current?.instance(),
      }).then(() => {
        doc.save('Tasks.pdf');
      });
    } else {
      exportGanttToPdf(
        {
          component: ganttRef.current?.instance(),
          createDocumentMethod: (args) => new jsPDF(args),
        },
      ).then((doc) => doc.save('gantt.pdf'));
    }
  }, [view]);

  const exportToXSLX = useCallback(() => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGridXSLX({
      component: gridRef.current?.instance(),
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
      });
    });
  }, []);

  const search = useCallback((e: TextBoxTypes.InputEvent) => {
    gridRef.current?.instance().searchByText(e.component.option('text') ?? '');
  }, []);

  const getTabsWidth = useCallback(() => {
    return isXSmall ? 220 : 'auto';
  }, []);

  return (
    <div className='view-wrapper view-wrapper-task-list list-page'>
      <div className='task-page-header'>
        <PageBreadcrumb title='Danh sách công việc' />
        <Toolbar className='toolbar-common task-actions-toolbar'>
          <Item
            location='before'
            widget='dxButton'
            locateInMenu='auto'
          >
            <Button
              icon='plus'
              text='Add Task'
              type='default'
              stylingMode='contained'
              onClick={onAddTaskClick}
            />
          </Item>
          <Item
            location='before'
            widget='dxButton'
            showText='inMenu'
            locateInMenu='auto'
          >
            <Button
              icon='refresh'
              text='Refresh'
              stylingMode='text'
              onClick={refresh}
            />
          </Item>
          <Item
            location='before'
            widget='dxButton'
            showText='inMenu'
            locateInMenu='auto'
            disabled={view !== listView}
          >
            <Button
              icon='columnchooser'
              text='Column Chooser'
              stylingMode='text'
              onClick={showColumnChooser}
            />
          </Item>
          <Item location='before' locateInMenu='auto'>
            <div className='separator' />
          </Item>
          <Item
            location='before'
            widget='dxButton'
            showText='inMenu'
            locateInMenu='auto'
            disabled={isKanban}
          >
            <Button
              icon='exportpdf'
              text='Export To PDF'
              stylingMode='text'
              onClick={exportToPDF}
            />
          </Item>
          <Item
            location='before'
            widget='dxButton'
            showText='inMenu'
            locateInMenu='auto'
            disabled={view !== listView}
          >
            <Button
              icon='exportxlsx'
              text='Export To XSLX'
              stylingMode='text'
              onClick={exportToXSLX}
            />
          </Item>
          <Item
            location='before'
            widget='dxTextBox'
            locateInMenu='auto'
            disabled={view !== listView}
          >
            <TextBox
              mode='search'
              placeholder='Task Search'
              onInput={search}
            />
          </Item>
        </Toolbar>
      </div>
      <div className='task-view-tabs'>
        <Tabs
          dataSource={listsData}
          width={getTabsWidth()}
          selectedIndex={index}
          scrollByContent
          showNavButtons={false}
          onItemClick={onTabClick}
        />
      </div>
      {loading && <LoadPanel container='.content' showPane={false} visible position={{ of: '.content' }} />}
      {!loading && isDataGrid && <TaskListGrid dataSource={gridData} ref={gridRef} />}
      {!loading && isKanban && <TaskListKanban dataSource={filteredData} ref={kanbanRef} changePopupVisibility={() => changePopupVisibility(!popupVisible)} />}
      {!loading && view === ganttView && <TaskListGantt dataSource={filteredData} ref={ganttRef} />}
      <FormPopup title='New Task' visible={popupVisible} setVisible={changePopupVisibility} onSave={onSaveClick}>
        <TaskFormDetails subjectField data={formTaskInitData} editing onDataChanged={onDataChanged} />
      </FormPopup>
    </div>
  );
};
